import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { festivalsAPI, handleApiError } from '../services/api';
import { showSuccess, showError } from '../utils/toast';
import type { Festival, FestivalStatus } from '../types';
import { FESTIVAL_STATUS_VALUES } from '../types/festival.types';
import FestivalForm from './FestivalForm';
import './Festivals.css';

type ViewMode =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'edit'; festivalId: string };

const statusBadgeClass = (status: FestivalStatus): string => {
  switch (status) {
    case 'DRAFT':
      return 'festival-status-badge status-draft';
    case 'PUBLISHED':
      return 'festival-status-badge status-published';
    case 'CANCELLED':
      return 'festival-status-badge status-cancelled';
    case 'ARCHIVED':
      return 'festival-status-badge status-archived';
    default:
      return 'festival-status-badge status-draft';
  }
};

const formatDate = (iso: string): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('bg-BG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const Festivals: React.FC = () => {
  const [festivals, setFestivals] = useState<Festival[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>({ kind: 'list' });

  const loadFestivals = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await festivalsAPI.listFestivals(1, 100);
      // Backend returns List[FestivalResponse] directly.
      setFestivals(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view.kind === 'list') {
      loadFestivals();
    }
  }, [view.kind, loadFestivals]);

  const handleStatusChange = async (
    festival: Festival,
    newStatus: FestivalStatus
  ): Promise<void> => {
    if (newStatus === festival.status) return;
    try {
      const res = await festivalsAPI.setFestivalStatus(festival.id, {
        status: newStatus,
      });
      const updated = res.data;
      setFestivals((prev) =>
        prev.map((f) => (f.id === updated.id ? updated : f))
      );
      showSuccess(`Статусът на „${updated.name}" е „${updated.status}"`);
    } catch (err) {
      showError('Грешка при смяна на статус: ' + handleApiError(err));
    }
  };

  if (view.kind === 'create') {
    return (
      <FestivalForm
        mode="create"
        onCancel={() => setView({ kind: 'list' })}
        onSaved={() => setView({ kind: 'list' })}
      />
    );
  }

  if (view.kind === 'edit') {
    return (
      <FestivalForm
        mode="edit"
        festivalId={view.festivalId}
        onCancel={() => setView({ kind: 'list' })}
        onSaved={() => setView({ kind: 'list' })}
      />
    );
  }

  return (
    <div className="festivals-page">
      <div className="festivals-toolbar">
        <div>
          <h2 style={{ margin: 0, color: 'var(--color-text-primary)' }}>Фестивали</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: 14 }}>
            Управление на фестивали (всички статуси, включително DRAFT)
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setView({ kind: 'create' })}
          data-testid="create-festival-btn"
        >
          <i className="fas fa-plus"></i>
          Нов фестивал
        </button>
      </div>

      {error && (
        <div className="festivals-error" role="alert">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="festivals-loading">
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 32 }}></i>
          <p>Зареждане на фестивали…</p>
        </div>
      ) : festivals.length === 0 ? (
        <div className="festivals-empty">
          <i className="fas fa-calendar-alt" style={{ fontSize: 48, opacity: 0.4 }}></i>
          <p>Все още няма създадени фестивали.</p>
        </div>
      ) : (
        <div className="festivals-table-wrap">
          <table className="festivals-table" data-testid="festivals-table">
            <thead>
              <tr>
                <th>Име</th>
                <th>Slug</th>
                <th>Статус</th>
                <th>Начало</th>
                <th>Край</th>
                <th>Място</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {festivals.map((f) => (
                <tr key={f.id}>
                  <td>
                    <div className="festival-name">{f.name}</div>
                    {f.name_en && (
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        {f.name_en}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="festival-slug">{f.slug}</span>
                  </td>
                  <td>
                    <span className={statusBadgeClass(f.status)}>{f.status}</span>
                  </td>
                  <td>{formatDate(f.starts_at)}</td>
                  <td>{formatDate(f.ends_at)}</td>
                  <td>{f.venue_name || '—'}</td>
                  <td>
                    <div className="festival-row-actions">
                      <button
                        onClick={() => setView({ kind: 'edit', festivalId: f.id })}
                        title="Редактирай"
                      >
                        <i className="fas fa-edit"></i> Редактирай
                      </button>
                      <select
                        value={f.status}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                          handleStatusChange(f, e.target.value as FestivalStatus)
                        }
                        title="Промени статус"
                        aria-label={`Статус на ${f.name}`}
                      >
                        {FESTIVAL_STATUS_VALUES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Festivals;
